import type {
  Calendar,
  CalendarColor,
  Controller,
  GraphDatabase,
  GraphDatabaseDictionary,
  GraphResult,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import { getColor, resolveLabelColor } from "@/components/color";
import type { BrowserStore } from "@/components/graph/grouping";
import {
  asRecordSchemaValue,
  asRecordType,
  databaseForTable,
  isLabelsRelationField,
  isMultiSelectField,
  isSelectField,
  relationTargetDatabase,
  selectFieldValues,
  type RecordSchemaField,
  type RecordSchemaValue,
} from "@/components/graph/predicates";
import { selectConfigOfField } from "@/components/graph/provisioned";
import { getGraphQuery, runCompiledQuery } from "@/components/graph/query";
import { asArray, asString } from "@/components/graph/result";
import { labelsFromRelationResult } from "@/components/labels";
import {
  definitiveTableId,
  EVENTS_TABLE_ID,
  TASKS_TABLE_ID,
} from "@/components/tables";
import { releasableRef } from "@/ref";
import { arrayProperty, isRecord, property } from "@/utils/unknown";

/** Synthetic option: color comes from the calendar the event or allocation lives in. */
export const CALENDAR_COLOR_SOURCE_ID = "calendar";

export const EVENT_COLOR_SOURCE_STORAGE_KEY = "calendars/colors/events";
export const TASK_COLOR_SOURCE_STORAGE_KEY = "calendars/colors/tasks";

/**
 * Where events or tasks take their color from (the persisted Calendars
 * setting): the calendar they live in, the row's Labels column (`labels`,
 * the multi-relation to the Labels database), or any other property
 * (`path`) reached by following relation columns — every id but the last
 * is a hop, so a single id is a direct property of the row.
 */
export type ColorSource =
  | { kind: "calendar" }
  | { kind: "labels"; propertyId: string }
  | { kind: "path"; propertyIds: string[] };

export function calendarColorSource(): ColorSource {
  return { kind: "calendar" };
}

/** A persisted value as a {@link ColorSource}, or `undefined` when malformed. */
export function asColorSource(value: unknown): ColorSource | undefined {
  if (!isRecord(value)) return undefined;
  switch (value.kind) {
    case "calendar":
      return { kind: "calendar" };
    case "labels": {
      const propertyId = value.propertyId;
      return typeof propertyId === "string" && propertyId
        ? { kind: "labels", propertyId }
        : undefined;
    }
    case "path": {
      const raw = arrayProperty(value, "propertyIds");
      if (!raw || raw.length === 0) return undefined;
      const propertyIds: string[] = [];
      for (const item of raw) {
        if (typeof item !== "string" || !item) return undefined;
        propertyIds.push(item);
      }
      return { kind: "path", propertyIds };
    }
    default:
      return undefined;
  }
}

/**
 * The property ids to walk from the row to the color-bearing property, or
 * `undefined` for the calendar source.
 */
export function colorSourcePropertyIds(
  source: ColorSource
): string[] | undefined {
  switch (source.kind) {
    case "calendar":
      return undefined;
    case "labels":
      return [source.propertyId];
    case "path":
      return source.propertyIds;
  }
}

/**
 * The source selected as a property-id path in the picker, where
 * `[CALENDAR_COLOR_SOURCE_ID]` is the synthetic Calendar option. A single
 * id is stored as `labels` when it is the row's Labels column, as a
 * one-hop `path` otherwise.
 */
export function colorSourceFromPropertyPath(
  path: string[],
  database: GraphDatabase | undefined
): ColorSource {
  if (path.length === 1 && path[0] === CALENDAR_COLOR_SOURCE_ID) {
    return { kind: "calendar" };
  }
  if (path.length === 1) {
    const field = asRecordType(database?.type)?.fields[path[0]];
    if (field && isLabelsRelationField(field)) {
      return { kind: "labels", propertyId: path[0] };
    }
  }
  return { kind: "path", propertyIds: [...path] };
}

/** Inverse of {@link colorSourceFromPropertyPath}, for the picker's model. */
export function colorSourcePropertyPath(source: ColorSource): string[] {
  return colorSourcePropertyIds(source) ?? [CALENDAR_COLOR_SOURCE_ID];
}

/**
 * Human-readable description of a color source: "Calendar", the Labels
 * property's name, or the field names along a path.
 */
export function colorSourceLabels(
  types: GraphDatabaseDictionary | undefined,
  database: GraphDatabase | undefined,
  source: ColorSource
): string[] {
  if (source.kind === "calendar") return ["Calendar"];
  if (source.kind === "labels") {
    const field = asRecordType(database?.type)?.fields[source.propertyId];
    return [field?.name ?? "Labels"];
  }
  const labels: string[] = [];
  let current = database;
  for (const id of source.propertyIds) {
    const field = asRecordType(current?.type)?.fields[id];
    labels.push(field?.name ?? id);
    current = field ? relationTargetDatabase(types, field) : undefined;
  }
  return labels;
}

/**
 * The row's Labels property (the multi-relation to the Labels database),
 * i.e. what a `labels` color source points at. Used to pin Labels as a
 * dedicated picker option instead of listing it among normal properties.
 */
export function labelsColorProperty(
  database: GraphDatabase | undefined
): { id: string; name: string } | undefined {
  const fields = asRecordType(database?.type)?.fields;
  if (!fields) return undefined;
  for (const [id, field] of Object.entries(fields)) {
    if (isLabelsRelationField(field)) {
      return { id, name: field.name ?? "Labels" };
    }
  }
  return undefined;
}

/** Event Status is the active/passive control, not a color source. */
function isEventStatusField(
  database: GraphDatabase | undefined,
  field: RecordSchemaField
): boolean {
  return (
    field.name === "Status" &&
    database !== undefined &&
    definitiveTableId(database.id) === EVENTS_TABLE_ID
  );
}

/** Task Status is Todo/Completed/Discarded, not a color source. */
function isTaskStatusField(
  database: GraphDatabase | undefined,
  field: RecordSchemaField
): boolean {
  return (
    field.name === "Status" &&
    database !== undefined &&
    definitiveTableId(database.id) === TASKS_TABLE_ID
  );
}

/**
 * Whether a field is a valid end of a color-source path: a single- or
 * multi-select, or a relation to the Labels database — excluding Event and
 * Task Status, which look like selects but are lifecycle controls.
 */
export function isColorSourceField(
  database: GraphDatabase | undefined,
  field: RecordSchemaField
): boolean {
  if (isEventStatusField(database, field) || isTaskStatusField(database, field))
    return false;
  return (
    isSelectField(field) ||
    isMultiSelectField(field) ||
    isLabelsRelationField(field)
  );
}

/** The end of a color-source path once resolved against the schema. */
export type ColorSourceTarget = {
  /** Database owning the end field. */
  database: GraphDatabase;
  fieldId: string;
  field: RecordSchemaField;
};

/**
 * Walk a (non-Calendar) source through the schema: every hop but the last
 * must be a relation column to a known type, the last a valid color-source
 * field (for a `labels` source, specifically a Labels relation).
 * `undefined` when any hop no longer exists.
 */
export function colorSourceTarget(
  types: GraphDatabaseDictionary | undefined,
  database: GraphDatabase | undefined,
  source: ColorSource
): ColorSourceTarget | undefined {
  const propertyIds = colorSourcePropertyIds(source);
  if (!propertyIds || propertyIds.length === 0) return undefined;
  let current = database;
  for (const hop of propertyIds.slice(0, -1)) {
    const field = asRecordType(current?.type)?.fields[hop];
    current = field ? relationTargetDatabase(types, field) : undefined;
  }
  if (!current) return undefined;
  const fieldId = propertyIds[propertyIds.length - 1];
  const field = asRecordType(current.type)?.fields[fieldId];
  if (!field || !isColorSourceField(current, field)) return undefined;
  if (source.kind === "labels" && !isLabelsRelationField(field)) {
    return undefined;
  }
  return { database: current, fieldId, field };
}

/**
 * The color source to use from a persisted value: the stored source when it
 * still resolves against the schema, Calendar otherwise.
 */
export function resolvedColorSource(
  stored: unknown,
  types: GraphDatabaseDictionary | undefined,
  database: GraphDatabase | undefined
): ColorSource {
  const source = asColorSource(stored);
  if (!source) return calendarColorSource();
  if (source.kind === "calendar") return source;
  return colorSourceTarget(types, database, source)
    ? source
    : calendarColorSource();
}

/**
 * Load-time resolution: malformed JSON becomes Calendar immediately, but a
 * property or path is kept as-is until the type schemas are available
 * (`types` defined) so a valid choice is not overwritten while types are
 * still loading.
 */
export function loadedColorSource(
  stored: unknown,
  types: GraphDatabaseDictionary | undefined,
  database: GraphDatabase | undefined
): ColorSource {
  const source = asColorSource(stored);
  if (!source) return calendarColorSource();
  if (types === undefined) return source;
  return resolvedColorSource(source, types, database);
}

function selectValueColor(
  store: BrowserStore,
  database: GraphDatabase | undefined,
  fieldName: string,
  value: string
): CalendarColor {
  return (
    selectConfigOfField(store, database?.workspace, database, fieldName)
      ?.colors?.[value] ?? getColor(value)
  );
}

function fieldOptionValues(field: RecordSchemaField): string[] {
  const single = selectFieldValues(field);
  if (single) return single;
  if (!isMultiSelectField(field) || !field.value) return [];
  const elements = asRecordSchemaValue(property(field.value, "elements"));
  return elements ? (selectFieldValues({ value: elements }) ?? []) : [];
}

export type ColorPreviewSwatch = {
  color: CalendarColor;
  label: string;
};

/**
 * Colors (and names) to preview for a color-source choice: calendars, the
 * end field's select options, or Labels.
 */
export function colorSourcePreviewSwatches(args: {
  source: ColorSource;
  types: GraphDatabaseDictionary | undefined;
  database: GraphDatabase | undefined;
  store: BrowserStore;
  calendars: ReadonlyArray<Pick<Calendar, "color" | "summary">>;
  labels: ReadonlyArray<{ id: string; title: string }>;
}): ColorPreviewSwatch[] {
  const { source, types, database, store, calendars, labels } = args;
  if (source.kind === "calendar") {
    return calendars.flatMap((calendar) =>
      calendar.color ? [{ color: calendar.color, label: calendar.summary }] : []
    );
  }
  const target = colorSourceTarget(types, database, source);
  if (!target) return [];
  const { field } = target;
  const fieldName = field.name ?? target.fieldId;
  if (isSelectField(field) || isMultiSelectField(field)) {
    return fieldOptionValues(field).map((value) => ({
      color: selectValueColor(store, target.database, fieldName, value),
      label: value,
    }));
  }
  if (isLabelsRelationField(field)) {
    return labels.map((label) => ({
      color: resolveLabelColor(store, label.id, label.title),
      label: label.title,
    }));
  }
  return [];
}

/** Whether the schema value holds a list (possibly behind variants/tuples). */
function containsListValue(value: RecordSchemaValue | undefined): boolean {
  if (!value) return false;
  switch (value.type) {
    case "list":
      return true;
    case "variant":
    case "tuple": {
      const children = property(
        value,
        value.type === "variant" ? "cases" : "elements"
      );
      if (!Array.isArray(children)) return false;
      return children.some((child) =>
        containsListValue(asRecordSchemaValue(child))
      );
    }
    default:
      return false;
  }
}

/** One hop of a color-source query: the field's name, fanning out when a list. */
type ColorSourceQueryHop = { name: string; list: boolean };

/**
 * The schema names to walk in a graph query for a (non-Calendar) source —
 * query results key cells by field *name*, not column id. `undefined` when
 * the source no longer resolves or a name cannot be quoted safely.
 */
function colorSourceQueryHops(
  types: GraphDatabaseDictionary | undefined,
  database: GraphDatabase | undefined,
  source: ColorSource
): ColorSourceQueryHop[] | undefined {
  if (!colorSourceTarget(types, database, source)) return undefined;
  const propertyIds = colorSourcePropertyIds(source);
  if (!propertyIds) return undefined;
  const hops: ColorSourceQueryHop[] = [];
  let current = database;
  for (const id of propertyIds) {
    const field = asRecordType(current?.type)?.fields[id];
    const name = field?.name;
    if (!field || !name || name.includes("'")) return undefined;
    hops.push({ name, list: containsListValue(field.value) });
    current = relationTargetDatabase(types, field);
  }
  return hops;
}

/**
 * Chained accesses for `hops` continuing the expression `prefix`: `.?`
 * keeps a missing hop null instead of failing the query, and a list hop
 * fans out with `select` (field access does not distribute over lists).
 */
function hopsExpression(hops: ColorSourceQueryHop[], prefix: string): string {
  const [hop, ...rest] = hops;
  return continueExpression(hop, rest, `${prefix}.?'${hop.name}'`);
}

/** Like {@link hopsExpression} inside a `select`, where the element's fields are in scope as bare names. */
function scopedHopsExpression(hops: ColorSourceQueryHop[]): string {
  const [hop, ...rest] = hops;
  return continueExpression(hop, rest, `'${hop.name}'`);
}

function continueExpression(
  hop: ColorSourceQueryHop,
  rest: ColorSourceQueryHop[],
  access: string
): string {
  if (rest.length === 0) return access;
  if (!hop.list) return hopsExpression(rest, access);
  return `${access} select (${scopedHopsExpression(rest)})`;
}

/**
 * Graph query fetching the cell a color source reads on one row —
 * traversing relations server-side, since a plain row fetch only embeds
 * related rows shallowly (title only). `undefined` when the source is
 * Calendar or no longer resolves against the schema.
 */
export function colorSourceCellQuery(args: {
  source: ColorSource;
  types: GraphDatabaseDictionary | undefined;
  database: GraphDatabase | undefined;
  tableId: string;
  rowId: string;
}): string | undefined {
  const hops = colorSourceQueryHops(args.types, args.database, args.source);
  if (!hops || hops.length === 0) return undefined;
  return hopsExpression(
    hops,
    `databases.["${args.tableId}"].rows["${args.rowId}"]`
  );
}

/**
 * Fetch the value a color source reads on an event or task extension row.
 * An empty ref when nothing needs fetching (Calendar source, unset row) or
 * the source no longer resolves.
 *
 * Never throws: color resolution must always be able to fall back to the
 * calendar color, so any query failure — including a rejected RPC — yields
 * an empty ref instead of failing the caller's setup.
 */
export async function fetchColorSourceCell(
  controller: Controller,
  workspaceId: string | undefined,
  tableId: string,
  rowId: string | null | undefined,
  args: {
    source: ColorSource;
    types: GraphDatabaseDictionary | undefined;
    database: GraphDatabase | undefined;
  }
): Promise<ReleasableRef<GraphResult | undefined>> {
  if (!workspaceId || !rowId) return releasableRef(undefined);
  const queryText = colorSourceCellQuery({ ...args, tableId, rowId });
  if (!queryText) return releasableRef(undefined);
  try {
    const query = await getGraphQuery(workspaceId, queryText, controller);
    if (!query) return releasableRef(undefined);
    const result = await runCompiledQuery(query, controller);
    return result.map((value) => value ?? undefined);
  } catch {
    return releasableRef(undefined);
  }
}

/**
 * Non-null leaves of a fetched cell: `select` fan-outs nest lists (and
 * multi-selects are lists themselves), so flatten arrays and drop the
 * nulls of missing hops.
 */
function cellLeaves(cell: GraphResult): GraphResult[] {
  if (cell === null) return [];
  const items = asArray(cell);
  if (!items) return [cell];
  return items.flatMap(cellLeaves);
}

/**
 * Colors carried by a fetched color-source cell, one per select value or
 * related label. Empty when the caller should use the calendar color
 * (Calendar source, missing value, or a field that is no longer a color
 * source).
 */
export function sourceColorsFromCell(args: {
  source: ColorSource;
  cell: GraphResult | null | undefined;
  database: GraphDatabase | undefined;
  types: GraphDatabaseDictionary | undefined;
  store: BrowserStore;
}): CalendarColor[] {
  const { source, cell, database, types, store } = args;
  if (cell == null) return [];
  const target = colorSourceTarget(types, database, source);
  if (!target) return [];
  const { field } = target;
  const fieldName = field.name ?? target.fieldId;
  if (isSelectField(field) || isMultiSelectField(field)) {
    return cellLeaves(cell).flatMap((leaf) => {
      const value = asString(leaf);
      return value
        ? [selectValueColor(store, target.database, fieldName, value)]
        : [];
    });
  }
  if (isLabelsRelationField(field)) {
    return cellLeaves(cell).flatMap((leaf) =>
      labelsFromRelationResult(leaf).map(({ id, title }) =>
        resolveLabelColor(store, id, title)
      )
    );
  }
  return [];
}

/**
 * First color from {@link sourceColorsFromCell}, or `undefined` to use the
 * calendar color.
 */
export function sourceColorFromCell(args: {
  source: ColorSource;
  cell: GraphResult | null | undefined;
  database: GraphDatabase | undefined;
  types: GraphDatabaseDictionary | undefined;
  store: BrowserStore;
}): CalendarColor | undefined {
  return sourceColorsFromCell(args)[0];
}

/** CSS class for a color-source fill (`calendar-a--light`). */
export function sourceColorClass(args: {
  source: ColorSource;
  cell: GraphResult | null | undefined;
  database: GraphDatabase | undefined;
  types: GraphDatabaseDictionary | undefined;
  store: BrowserStore;
  calendar: Pick<Calendar, "color"> | null | undefined;
  theme: "light" | "dark";
}): string {
  const color = sourceColorFromCell(args) ?? args.calendar?.color;
  return color ? `calendar-${color}--${args.theme}` : "calendar-placeholder";
}

/**
 * Resolve the color source for an event: allocations color from the task's
 * cell, database, and Calendars setting; plain events from the event's.
 */
function resolveColorSource(args: {
  isAllocation: boolean;
  types: GraphDatabaseDictionary | undefined;
  sources: { events: ColorSource; tasks: ColorSource };
  eventCell: GraphResult | null | undefined;
  taskCell: GraphResult | null | undefined;
}): {
  source: ColorSource;
  cell: GraphResult | null | undefined;
  database: GraphDatabase | undefined;
} {
  const tableId = args.isAllocation ? TASKS_TABLE_ID : EVENTS_TABLE_ID;
  const database = databaseForTable(args.types, tableId);
  return {
    source: loadedColorSource(
      args.isAllocation ? args.sources.tasks : args.sources.events,
      args.types,
      database
    ),
    cell: args.isAllocation ? args.taskCell : args.eventCell,
    database,
  };
}

/**
 * Color of an event or allocation, using the Calendars Color setting: the
 * source's color when it resolves, the calendar's own color otherwise.
 */
export function colorFromSources(args: {
  isAllocation: boolean;
  types: GraphDatabaseDictionary | undefined;
  sources: { events: ColorSource; tasks: ColorSource };
  eventCell: GraphResult | null | undefined;
  taskCell: GraphResult | null | undefined;
  store: BrowserStore;
  calendar: Pick<Calendar, "color"> | null | undefined;
}): CalendarColor | undefined {
  return (
    sourceColorFromCell({
      ...resolveColorSource(args),
      types: args.types,
      store: args.store,
    }) ?? args.calendar?.color
  );
}

/** Fill class for an event or allocation, from {@link colorFromSources}. */
export function colorClassFromSources(args: {
  isAllocation: boolean;
  types: GraphDatabaseDictionary | undefined;
  sources: { events: ColorSource; tasks: ColorSource };
  eventCell: GraphResult | null | undefined;
  taskCell: GraphResult | null | undefined;
  store: BrowserStore;
  calendar: Pick<Calendar, "color"> | null | undefined;
  theme: "light" | "dark";
}): string {
  const color = colorFromSources(args);
  return color ? `calendar-${color}--${args.theme}` : "calendar-placeholder";
}
